import * as XLSX from 'xlsx';
import { collection, doc, setDoc, writeBatch } from 'firebase/firestore';
import { db, getGeminiModel, firebaseConfig } from '../firebase/config';
import {
  ExtractedGstInvoice,
  Gstr2bRecord,
  InvoiceCategory,
  GstUploadMetadata
} from '../types/gst';
import { validateAndCalculateInvoiceMath, normalizeInvoiceNumber } from './mathValidator';
import { classifyInvoiceCompliance } from './complianceRagService';
import { buildGeminiExtractionPrompt, applyGstSystemInstructions, resolveStatutoryTaxPeriod } from './gstSystemInstructions';

/**
 * Computes SHA-256 hash of a file using Web Cryptography API
 */
export async function computeFileSha256(file: File): Promise<string> {
  const arrayBuffer = await file.arrayBuffer();
  const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Converts File to Base64 data string
 */
export async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const base64 = result.split(',')[1];
      resolve(base64 || "");
    };
    reader.onerror = (error) => reject(error);
    reader.readAsDataURL(file);
  });
}

/**
 * Calls Gemini directly using Google AI REST API with active key or Vertex AI
 */
async function callGeminiVision(
  base64Data: string,
  mimeType: string,
  prompt: string
): Promise<string> {
  const geminiApiKey =
    (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_GEMINI_API_KEY) ||
    'AQ.Ab8RN6Ky-Lig_Jgl-8PCIIIydta6K-nzBGDnjwvG6B4Bzm_u9A';
  const modelName = 'gemini-3.8-flash';

  const endpoint = `https://generativelanguage.googleapis.com/v1beta/models/${modelName}:generateContent?key=${geminiApiKey}`;

  const payload = {
    contents: [
      {
        parts: [
          { text: prompt },
          {
            inlineData: {
              mime_type: mimeType,
              data: base64Data
            }
          }
        ]
      }
    ],
    generationConfig: {
      temperature: 0.1,
      responseMimeType: "application/json"
    }
  };

  const resp = await fetch(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });

  if (!resp.ok) {
    const errorText = await resp.text();
    const vertexModel = getGeminiModel(modelName);
    if (vertexModel) {
      try {
        const result = await vertexModel.generateContent([
          prompt,
          {
            inlineData: {
              data: base64Data,
              mimeType: mimeType
            }
          }
        ]);
        const text = result.response.text();
        if (text) return text;
      } catch (vertexErr) {
        console.warn("Vertex AI fallback also failed:", vertexErr);
      }
    }
    throw new Error(`Gemini Vision API error (${resp.status}): ${errorText}`);
  }

  const json = await resp.json();
  const rawText = json.candidates?.[0]?.content?.parts?.[0]?.text;
  if (!rawText) throw new Error("Empty response from Gemini Vision");
  return rawText;
}



/**
 * Multimodal AI Extraction for PDF / JPG / PNG Tax Invoices & Debit Notes
 */
export async function extractInvoiceFromDocument(
  file: File,
  clientId: string,
  financialYear: string,
  taxPeriod: string,
  category: InvoiceCategory,
  storagePath: string,
  downloadUrl?: string
): Promise<ExtractedGstInvoice> {
  const base64Data = await fileToBase64(file);
  const mimeType = file.type || (file.name.endsWith('.pdf') ? 'application/pdf' : 'image/jpeg');

  const prompt = `
You are an expert Indian GST Tax Invoice & Debit Note OCR Auditor.
Extract structured fields from this Indian Tax Invoice, Debit Note, or Expense Receipt document with 100% precision.
Return ONLY valid JSON with this exact structure:
{
  "supplier_gstin": "15-char GSTIN string or empty if unregistered",
  "supplier_name": "Full legal or trade name of vendor/supplier",
  "buyer_gstin": "15-char recipient GSTIN string if present",
  "buyer_name": "Full legal name of buyer if present",
  "document_type": "INVOICE or DEBIT_NOTE or CREDIT_NOTE or BILL_OF_SUPPLY",
  "invoice_number": "Raw invoice or debit note number as printed",
  "invoice_date": "YYYY-MM-DD",
  "place_of_supply": "2-digit state code or State name (e.g. '09 Uttar Pradesh')",
  "reverse_charge_applicable": false,
  "is_reimbursement_or_pure_agent": false,
  "invoice_total": 0.00,
  "line_items": [
    {
      "description": "Item description",
      "hsn_sac_code": "HSN or SAC code",
      "qty": 1,
      "unit_price": 0.00,
      "taxable_value": 0.00,
      "cgst_rate": 0,
      "sgst_rate": 0,
      "igst_rate": 0,
      "cess_rate": 0
    }
  ]
}
CRITICAL ACCURACY RULES:
1. DO NOT HALLUCINATE TAXES! Extract the exact rates and tax amounts printed on the document.
2. If the document is a DEBIT NOTE, REIMBURSEMENT NOTE, or GOVERNMENT FEE REIMBURSEMENT (e.g. MCA filing fees, ROC fees, pure agent reimbursement) where NO GST / 0% GST is charged:
   - Set "cgst_rate": 0, "sgst_rate": 0, "igst_rate": 0, "cess_rate": 0 for all line items.
   - Set "is_reimbursement_or_pure_agent": true.
   - Set "reverse_charge_applicable": false.
   - Do NOT mark director sitting fees or reverse charge on government fee reimbursements.
3. Extract the line items accurately. If a single reimbursement amount (e.g. 900.00) is shown, taxable_value must be 900.00 and tax rates 0.
4. If supplier GSTIN or trade name is clearly printed (e.g. Startup Movers, GSTIN 09AAUCS0938E1ZZ), extract them accurately.
5. NEVER fabricate mock numbers or invent 18% GST.
`;

  let extractedRaw: any;
  try {
    const responseText = await callGeminiVision(base64Data, mimeType, prompt);
    extractedRaw = JSON.parse(responseText.replace(/```json/g, '').replace(/```/g, '').trim());
  } catch (err: any) {
    console.error("AI OCR extraction failed:", err);
    // DO NOT fabricate mock data! Throw an error with context so user can use the Split-Screen Manual Editor
    throw new Error("AI OCR Extraction Failed: " + (err?.message || "Could not parse document structure") + ". Please use the Manual Ingestion (Split View) form to review and enter the document fields.");
  }

  // Run Deterministic Math Validator
  const mathResult = validateAndCalculateInvoiceMath(extractedRaw);

  // Run Section 17(5) and RCM Compliance Classifier
  const compliance = classifyInvoiceCompliance({
    ...extractedRaw,
    category,
    line_items: mathResult.verifiedLineItems
  });

  const invoiceId = `inv_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;

  const extractedInvoice: ExtractedGstInvoice = {
    id: invoiceId,
    clientId,
    financialYear,
    taxPeriod,
    category,
    supplier_gstin: extractedRaw.supplier_gstin || "",
    supplier_name: extractedRaw.supplier_name || "Unknown Supplier",
    buyer_gstin: extractedRaw.buyer_gstin || "",
    invoice_number: extractedRaw.invoice_number || "INV-1",
    normalized_invoice_number: mathResult.normalizedInvoiceNumber,
    invoice_date: extractedRaw.invoice_date || new Date().toISOString().split('T')[0],
    place_of_supply: extractedRaw.place_of_supply || "",
    reverse_charge_applicable: compliance.reverseChargeApplicable,
    is_reimbursement_or_pure_agent: Boolean(extractedRaw.is_reimbursement_or_pure_agent || compliance.itcEligibility === 'INELIGIBLE' && mathResult.totalTax === 0),
    document_type: extractedRaw.document_type || 'INVOICE',
    line_items: mathResult.verifiedLineItems,
    total_taxable_value: mathResult.totalTaxableValue,
    total_tax: mathResult.totalTax,
    invoice_total: mathResult.calculatedInvoiceTotal,
    math_verified: mathResult.mathVerified,
    itc_eligibility: compliance.itcEligibility,
    ai_compliance_note: compliance.aiComplianceNote,
    recon_status: 'UNMATCHED',
    source_file_path: storagePath,
    source_file_url: downloadUrl,
    created_at: new Date().toISOString(),
    audit_trail: [
      {
        action: 'AI_EXTRACTED',
        timestamp: new Date().toISOString(),
        userId: clientId,
        details: `Extracted via Gemini 3.8 Flash Vision. Math Verified: ${mathResult.mathVerified}. Type: ${extractedRaw.document_type || 'INVOICE'}`
      }
    ]
  };

  return extractedInvoice;
}

/**
 * Spreadsheet (XLSX / CSV) Bulk Ingestion Engine
 * Reads workbook, maps columns via lightweight sample analysis, and processes rows deterministically in bulk
 */
export async function extractInvoicesFromSpreadsheet(
  file: File,
  clientId: string,
  financialYear: string,
  taxPeriod: string,
  category: InvoiceCategory,
  storagePath: string,
  downloadUrl?: string
): Promise<ExtractedGstInvoice[]> {
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: 'array' });
  const firstSheetName = workbook.SheetNames[0];
  const sheet = workbook.Sheets[firstSheetName];
  const rows: any[] = XLSX.utils.sheet_to_json(sheet, { defval: "" });

  if (rows.length === 0) return [];

  // Identify column names by normalization
  const sampleRow = rows[0];
  const keys = Object.keys(sampleRow);

  const findKey = (candidates: string[]): string | undefined => {
    return keys.find(k => {
      const lower = k.toLowerCase().replace(/[^a-z0-9]/g, "");
      return candidates.some(c => lower.includes(c));
    });
  };

  const invNumKey = findKey(["invoiceno", "invno", "voucherno", "billno", "invoice#", "number"]);
  const dateKey = findKey(["date", "invdate", "invoicedate", "billdate"]);
  const gstinKey = findKey(["gstin", "suppliergstin", "partygstin", "vendor_gstin", "uin"]);
  const nameKey = findKey(["partyname", "vendor", "supplier", "particulars", "party", "customer", "name"]);
  const taxableKey = findKey(["taxable", "taxablevalue", "taxableamount", "basic", "amount"]);
  const taxKey = findKey(["totaltax", "taxamount", "tax"]);
  const igstKey = findKey(["igst", "integratedtax"]);
  const cgstKey = findKey(["cgst", "centraltax"]);
  const sgstKey = findKey(["sgst", "statetax"]);
  const totalKey = findKey(["total", "invoiceamount", "invoicevalue", "netamount", "grandtotal"]);
  const hsnKey = findKey(["hsn", "sac", "hsncode"]);

  const extractedInvoices: ExtractedGstInvoice[] = [];

  for (let idx = 0; idx < rows.length; idx++) {
    const row = rows[idx];
    const rawInvNum = String(invNumKey ? row[invNumKey] : `ROW-${idx + 1}`).trim();
    if (!rawInvNum) continue;

    const supplierGstin = String(gstinKey ? row[gstinKey] : "").trim().toUpperCase();
    const supplierName = String(nameKey ? row[nameKey] : "Supplier").trim();
    const rawDate = String(dateKey ? row[dateKey] : new Date().toISOString().split('T')[0]).trim();
    const taxableVal = parseFloat(taxableKey ? row[taxableKey] : 0) || 0;
    const igstVal = parseFloat(igstKey ? row[igstKey] : 0) || 0;
    const cgstVal = parseFloat(cgstKey ? row[cgstKey] : 0) || 0;
    const sgstVal = parseFloat(sgstKey ? row[sgstKey] : 0) || 0;
    const rawTotal = parseFloat(totalKey ? row[totalKey] : 0) || (taxableVal + igstVal + cgstVal + sgstVal);
    const hsnCode = String(hsnKey ? row[hsnKey] : "9983").trim();

    // Rates estimation
    const igstRate = taxableVal > 0 && igstVal > 0 ? Math.round((igstVal / taxableVal) * 100) : 0;
    const cgstRate = taxableVal > 0 && cgstVal > 0 ? Math.round((cgstVal / taxableVal) * 100) : 0;
    const sgstRate = taxableVal > 0 && sgstVal > 0 ? Math.round((sgstVal / taxableVal) * 100) : 0;

    const lineItems = [
      {
        description: supplierName || "Sales / Purchase Item",
        hsn_sac_code: hsnCode,
        qty: 1,
        unit_price: taxableVal,
        taxable_value: taxableVal,
        cgst_rate: cgstRate,
        sgst_rate: sgstRate,
        igst_rate: igstRate,
        cess_rate: 0,
        cgst_amount: cgstVal,
        sgst_amount: sgstVal,
        igst_amount: igstVal,
        cess_amount: 0
      }
    ];

    const mathResult = validateAndCalculateInvoiceMath({
      invoice_number: rawInvNum,
      invoice_total: rawTotal,
      line_items: lineItems
    });

    const compliance = classifyInvoiceCompliance({
      supplier_gstin: supplierGstin,
      supplier_name: supplierName,
      category,
      line_items: mathResult.verifiedLineItems
    });

    const invoiceId = `inv_xl_${Date.now()}_${idx}_${Math.random().toString(36).substring(2, 6)}`;

    extractedInvoices.push({
      id: invoiceId,
      clientId,
      financialYear,
      taxPeriod,
      category,
      supplier_gstin: supplierGstin,
      supplier_name: supplierName,
      buyer_gstin: "",
      invoice_number: rawInvNum,
      normalized_invoice_number: mathResult.normalizedInvoiceNumber,
      invoice_date: rawDate,
      place_of_supply: supplierGstin ? supplierGstin.substring(0, 2) : "",
      reverse_charge_applicable: compliance.reverseChargeApplicable,
      line_items: mathResult.verifiedLineItems,
      total_taxable_value: mathResult.totalTaxableValue,
      total_tax: mathResult.totalTax,
      invoice_total: mathResult.calculatedInvoiceTotal,
      math_verified: mathResult.mathVerified,
      itc_eligibility: compliance.itcEligibility,
      ai_compliance_note: compliance.aiComplianceNote,
      recon_status: 'UNMATCHED',
      source_file_path: storagePath,
      source_file_url: downloadUrl,
      created_at: new Date().toISOString(),
      audit_trail: [
        {
          action: 'SPREADSHEET_INGESTED',
          timestamp: new Date().toISOString(),
          userId: clientId,
          details: `Ingested from ${file.name} row #${idx + 1}`
        }
      ]
    });
  }

  return extractedInvoices;
}

/**
 * GSTR-2B JSON Direct Parser
 * Parses official GSTN GSTR-2B JSON format (b2b, cdnr) directly in TypeScript
 */
export async function parseGstr2bJson(
  file: File,
  clientId: string,
  financialYear: string,
  taxPeriod: string,
  storagePath: string
): Promise<Gstr2bRecord[]> {
  const text = await file.text();
  const json = JSON.parse(text);

  const records: Gstr2bRecord[] = [];

  // GSTN GSTR-2B JSON can have root data or nested data.docdata
  const docdata = json.data?.docdata || json.docdata || json;
  const b2bList = docdata.b2b || [];

  for (const supplier of b2bList) {
    const ctin = String(supplier.ctin || "").trim().toUpperCase();
    const trdnm = String(supplier.trdnm || supplier.lgnm || "Vendor").trim();
    const invList = supplier.inv || [];

    for (const inv of invList) {
      const inum = String(inv.inum || "").trim();
      const idt = String(inv.idt || "").trim();
      const val = parseFloat(inv.val) || 0;
      const invTyp = String(inv.inv_typ || "R");
      const itcavl = inv.itcavl !== "N";
      const rsn = inv.rsn || "";

      let txval = 0;
      let iamt = 0;
      let camt = 0;
      let samt = 0;
      let csamt = 0;

      for (const item of inv.items || inv.itms || []) {
        const det = item.itm_det || item;
        txval += parseFloat(det.txval) || 0;
        iamt += parseFloat(det.iamt) || 0;
        camt += parseFloat(det.camt) || 0;
        samt += parseFloat(det.samt) || 0;
        csamt += parseFloat(det.csamt) || 0;
      }

      const recordId = `2b_${ctin}_${normalizeInvoiceNumber(inum)}_${Date.now()}`;

      records.push({
        id: recordId,
        clientId,
        financialYear,
        taxPeriod,
        supplier_gstin: ctin,
        supplier_name: trdnm,
        invoice_number: inum,
        normalized_invoice_number: normalizeInvoiceNumber(inum),
        invoice_type: invTyp,
        invoice_date: idt,
        invoice_value: val || (txval + iamt + camt + samt + csamt),
        taxable_value: Number(txval.toFixed(2)),
        integrated_tax: Number(iamt.toFixed(2)),
        central_tax: Number(camt.toFixed(2)),
        state_tax: Number(samt.toFixed(2)),
        cess_amount: Number(csamt.toFixed(2)),
        itc_available: itcavl,
        reason: rsn,
        source_file_path: storagePath,
        created_at: new Date().toISOString()
      });
    }
  }

  return records;
}

/**
 * Persists Extracted Invoices and GSTR-2B records in batches to Firestore
 */
export async function persistExtractionToFirestore(
  clientId: string,
  invoices: ExtractedGstInvoice[],
  records2b: Gstr2bRecord[],
  uploadMeta: GstUploadMetadata
): Promise<void> {
  const batch = writeBatch(db);

  // Write Upload doc
  const uploadRef = doc(db, 'clients', clientId, 'gst_uploads', uploadMeta.id);
  batch.set(uploadRef, {
    ...uploadMeta,
    extractedCount: invoices.length + records2b.length,
    status: 'COMPLETED'
  });

  // Write Invoices
  for (const inv of invoices) {
    const invRef = doc(db, 'clients', clientId, 'gst_invoices', inv.id);
    batch.set(invRef, inv);
  }

  // Write 2B records
  for (const rec of records2b) {
    const recRef = doc(db, 'clients', clientId, 'gst_2b_records', rec.id);
    batch.set(recRef, rec);
  }

  await batch.commit();
}
